import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useEventListener } from '@skin-seoul/utils';
import type { ReviewResponse } from '../../api/reviewApi';
import ToggleIcon from '../../assets/icons/ToggleIcon';
import { useCreateReview } from '../../hooks/useCreateReview';
import { usePageMeta } from '../../hooks/usePageMeta';
import { useProductReviews } from '../../hooks/useProductReviews';
import { Text1gMedium } from '../../styles/typography';
import { Spinner } from './components/common/Spinner';
import ReviewCard from './components/ReviewCard';
import ReviewFilterBar, { sortOptions } from './components/ReviewFilterBar';
import ReviewHeader from './components/ReviewHeader';
import ReviewPagination from './components/ReviewPagination';
import type { MobileReviewModalValues, BaseReviewModalValues } from './components/common/interfaces';
import { ReviewWriteMobileModal } from './components/ReviewWriteMobileModal';
import { ReviewWritePcModal } from './components/ReviewWritePcModal';
import { ImageGalleryModal } from './components/ImageGalleryModal';
import { cleanSkinData } from './utils/taxonomyUtils';

const Container = styled.div`
  width: 100%;
  background: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  box-sizing: border-box;

  @media (min-width: 768px) {
    max-width: 1280px;
    margin: 0 auto;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const Title = styled.div`
  ${Text1gMedium}
  color: #2e2e2e;
  height: 24px;
  margin: 0;

  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

const ContentWrapper = styled.div<{ isCollapsed: boolean }>`
  max-height: ${(props) => (props.isCollapsed ? '0' : '10000px')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`;

const Divider = styled.div`
  border: none;
  height: 1px;
  background-color: #e2e2e2;
  margin: 24px 0;

  @media (min-width: 768px) {
    margin: 32px 0;
  }
`;

const WriteReviewButton = styled.button`
  margin-top: 48px;
  width: 100%;
  height: 48px;
  background: #3d1690;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  font-family: 'Plus Jakarta Sans', sans-serif;
  display: block;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    opacity: 0.9;
  }

  @media (min-width: 768px) {
    max-width: 530px;
  }
`;

const ReviewList = styled.div<{ singleReview?: boolean }>`
  @media (min-width: 768px) {
    display: ${(props) => (props.singleReview ? 'block' : 'flex')};
    gap: 0px 32px;
    position: relative;
    align-items: start;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 1px;
      height: 100%;
      background: #e2e2e2;
      z-index: 1;
      display: ${(props) => (props.singleReview ? 'none' : 'block')};
    }
  }
`;

const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// Types
export interface ReviewImage {
  id: string;
  url: string;
  alt: string;
}

export interface Review {
  id: string;
  rating: number;
  title: string;
  content: string;
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  verifiedPurchase: boolean;
  createdAt: string;
  likeCount: number;
  skinType: string[];
  skinConcern: string[];
  images?: ReviewImage[];
}

export interface ReviewSectionProps {
  productId?: string;
}

// ReviewResponse -> Review 변환 함수
function mapReviewResponseToReview(r: ReviewResponse): Review {
  return {
    id: String(r.reviewId),
    rating: r.rating,
    title: r.title,
    content: r.body,
    author: {
      id: '', // API에 없음
      name: r.name,
      avatar: '', // API에 없음
    },
    verifiedPurchase: true, // API에 없음(임시)
    createdAt: r.modifiedAt,
    likeCount: 0, // API에 없음(임시)
    skinType: cleanSkinData(r.skinType),
    skinConcern: cleanSkinData(r.skinConcern),
    images: r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
    })),
  };
}

export const HomePageReviewSection = ({ productId }: ReviewSectionProps) => {
  const [currentPage, setCurrentPage] = useState(1);

  const [isPcWriteModalOpen, setIsPcWriteModalOpen] = useState(false);
  const [isMobileWriteOpen, setIsMobileWriteOpen] = useState(false);
  const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
  const [sort, setSort] = useState<string>(sortOptions.at(0)?.value || '');
  const [skinTypes, setSkinTypes] = useState<string[]>([]);
  const [skinConcerns, setSkinConcerns] = useState<string[]>([]);
  const isPC = useMediaQuery({ minWidth: 768 });
  const [isCollapsed, setIsCollapsed] = useState(false);
  // 리뷰 생성 mutation
  const { mutate: createReview, isPending: isCreatingReview } = useCreateReview();

  // postId fetch
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const { data: pageMeta, isLoading: isPageMetaLoading } = usePageMeta(path, { enabled: !isCollapsed });

  // Determine postId from productId prop or pageMeta
  const postId: number | undefined = (() => {
    if (productId) {
      return Number(productId);
    }
    if (pageMeta?.postId) {
      return Number(pageMeta.postId);
    }
    return undefined;
  })();

  // 리뷰 목록 조회 (React Query)
  const { data: reviewsRaw = [], isLoading: isReviewsLoading } = useProductReviews(postId!);
  const reviews = reviewsRaw.map(mapReviewResponseToReview);

  // 모든 리뷰의 pictureUrls를 합쳐서 customerImages 배열 생성
  const customerImages = reviewsRaw.flatMap((r) =>
    r.pictureUrls.map((url, idx) => ({
      id: `${r.reviewId}_${idx}`,
      url,
      alt: r.title,
    })),
  );

  const sortedAndFilteredReviews = useMemo(() => {
    const baseReviews = reviews || [];

    // Apply sorting
    const sortedReviews = (() => {
      switch (sort) {
        case 'newest':
          return [...baseReviews].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        case 'oldest':
          return [...baseReviews].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        case 'highest':
          return [...baseReviews].sort((a, b) => b.rating - a.rating);
        case 'lowest':
          return [...baseReviews].sort((a, b) => a.rating - b.rating);
        default:
          return baseReviews;
      }
    })();

    // Apply filters
    const result = sortedReviews
      .filter((review) => skinTypes.length === 0 || skinTypes.some((type) => review.skinType.includes(type)))
      .filter(
        (review) => skinConcerns.length === 0 || skinConcerns.some((concern) => review.skinConcern.includes(concern)),
      );

    return result;
  }, [reviews, sort, skinTypes, skinConcerns]);

  const [itemsPerPage, setItemsPerPage] = useState(8); // Increased default from 4 to 8

  const calculateItemsPerPage = useCallback(() => {
    if (typeof window === 'undefined') {
      return 4;
    }

    const viewportHeight = window.innerHeight;
    const headerHeight = 200; // Estimated space for header, filters etc.
    const footerHeight = 150; // Estimated space for pagination, write button
    const availableHeight = viewportHeight - headerHeight - footerHeight;

    // More conservative review height estimates
    const avgReviewWithImagesHeight = 300; // Reduced from 400
    const avgReviewWithoutImagesHeight = 180; // Reduced from 200

    // Use all reviews for calculation, not just filtered ones
    const allReviews = reviews || [];
    const reviewsWithImages = allReviews.filter((r) => r.images && r.images.length > 0).length;
    const reviewsWithoutImages = allReviews.length - reviewsWithImages;

    const avgReviewHeight =
      allReviews.length > 0
        ? (reviewsWithImages * avgReviewWithImagesHeight + reviewsWithoutImages * avgReviewWithoutImagesHeight) /
          allReviews.length
        : 200; // Default value when no reviews

    // Since we have 2 columns, divide by 2 to get items per column, then multiply by 2
    const itemsPerColumn = Math.floor(availableHeight / avgReviewHeight);
    const totalItems = Math.max(6, Math.min(16, itemsPerColumn * 2)); // Increased min from 4 to 6, max from 12 to 16

    return totalItems;
  }, [reviews]);

  // Update items per page when reviews or viewport changes
  const updateItemsPerPage = useCallback(() => {
    const newItemsPerPage = calculateItemsPerPage();
    setItemsPerPage(newItemsPerPage);
  }, [calculateItemsPerPage]);

  // Initial calculation when calculateItemsPerPage changes
  useEffect(() => {
    updateItemsPerPage();
  }, [updateItemsPerPage]);

  // Listen for window resize events using the hook
  useEventListener('resize', updateItemsPerPage);

  // Additional effect to recalculate when reviews finish loading
  useEffect(() => {
    if (!isReviewsLoading && reviews.length > 0) {
      const newItemsPerPage = calculateItemsPerPage();
      setItemsPerPage(newItemsPerPage);
    }
  }, [isReviewsLoading, reviews.length, calculateItemsPerPage]);

  const totalReviewsLength = reviews.length;
  const filteredTotalPages = Math.ceil((sortedAndFilteredReviews?.length || 0) / itemsPerPage);
  const averageRating =
    totalReviewsLength === 0
      ? 0
      : Number((reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviewsLength).toFixed(1));

  // ratingDistribution 계산 (1~5점별 개수)
  const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  reviews.forEach((r) => {
    const rating = Math.round(r.rating);
    if (rating >= 1 && rating <= 5) {
      ratingDistribution[rating] += 1;
    }
  });

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  // Calculate current reviews and split into columns efficiently
  const { currentReviews, leftColumnReviews, rightColumnReviews } = useMemo(() => {
    const slicedReviews = sortedAndFilteredReviews?.slice(startIndex, endIndex) || [];
    const leftColumn: Review[] = [];
    const rightColumn: Review[] = [];

    slicedReviews.forEach((review, index) => {
      if (index % 2 === 0) {
        leftColumn.push(review);
      } else {
        rightColumn.push(review);
      }
    });

    return {
      currentReviews: slicedReviews,
      leftColumnReviews: leftColumn,
      rightColumnReviews: rightColumn,
    };
  }, [sortedAndFilteredReviews, startIndex, endIndex]);

  const hasReviews = reviews.length > 0;
  const productImage = pageMeta?.image || '';

  // 모바일 리뷰 작성 모달이 열렸을 때 body 스크롤 막기
  useEffect(() => {
    if (!isPC && isMobileWriteOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileWriteOpen, isPC]);

  const handleClickWriteReview = () => {
    if (isPC) {
      setIsPcWriteModalOpen(true);
    } else {
      setIsMobileWriteOpen(true);
    }
  };

  const handleLikeClick = (reviewId: string) => {
    // TODO: Implement like functionality
  };

  const handleCloseWriteModal = useCallback(() => setIsPcWriteModalOpen(false), []);

  const handleCloseMobileWriteModal = useCallback(() => setIsMobileWriteOpen(false), []);

  const handleMobileReviewSubmit = useCallback(
    (values: MobileReviewModalValues) => {
      return new Promise<void>((resolve, reject) => {
        createReview(
          {
            name: values.nickname,
            rating: values.rating,
            body: values.content,
            productId: postId!,
            title: values.title,
            files: values.images,
            skinType: [values.skinType],
            skinConcern: [values.skinConcern],
          },
          {
            onSuccess: () => {
              resolve();
            },
            onError: (error) => {
              reject(new Error('Failed to create review: ' + (error?.message || 'Unknown error')));
            },
          },
        );
      });
    },
    [createReview, postId],
  );

  const handlePcReviewSubmit = useCallback(
    (values: BaseReviewModalValues) => {
      return new Promise<void>((resolve, reject) => {
        const files: File[] = [];
        createReview(
          {
            name: values.nickname,
            email: '', // 이메일 입력값 필요
            rating: values.rating,
            body: values.content,
            productId: postId!,
            title: values.title,
            files,
            skinType: [values.skinType],
            skinConcern: [values.skinConcern],
          },
          {
            onSuccess: () => {
              resolve();
            },
            onError: (error) => {
              reject(new Error('Failed to create review: ' + (error?.message || 'Unknown error')));
            },
          },
        );
      });
    },
    [createReview, postId],
  );

  const handleSortChange = (sortBy: string) => {
    setCurrentPage(1);
    setSort(sortBy);
  };

  const handleSkinTypeChange = (skinTypes: string[]) => {
    setCurrentPage(1);
    setSkinTypes(skinTypes);
  };

  const handleSkinConcernChange = (skinConcerns: string[]) => {
    setCurrentPage(1);
    setSkinConcerns(skinConcerns);
  };

  const handleViewAllImages = () => {
    setIsImageGalleryOpen(true);
  };

  const handleCloseImageGallery = () => {
    setIsImageGalleryOpen(false);
  };

  return (
    <Container id="judgeme_product_reviews">
      <TitleWrapper onClick={() => setIsCollapsed(!isCollapsed)}>
        <Title>Reviews</Title>
        <ToggleIcon isCollapsed={isCollapsed} />
      </TitleWrapper>

      <ContentWrapper isCollapsed={isCollapsed}>
        <ReviewHeader
          averageRating={averageRating}
          totalReviews={totalReviewsLength}
          ratingDistribution={ratingDistribution}
          customerImages={customerImages}
          reviews={reviews}
          onViewAllImages={handleViewAllImages}
        />

        {hasReviews && !isPageMetaLoading && !isReviewsLoading && (
          <>
            <Divider />

            <ReviewFilterBar
              onSortChange={handleSortChange}
              onSkinTypeChange={handleSkinTypeChange}
              onSkinConcernChange={handleSkinConcernChange}
            />

            <ReviewList singleReview={currentReviews.length === 1} style={{ alignSelf: 'start' }}>
              {currentReviews.length === 1 ? (
                <ReviewCard
                  key={currentReviews[0].id}
                  review={currentReviews[0]}
                  onLikeClick={handleLikeClick}
                  onImageClick={handleViewAllImages}
                  isLastInColumn={true}
                />
              ) : (
                <>
                  <Column>
                    {leftColumnReviews.map((review, columnIndex, columnArray) => (
                      <ReviewCard
                        key={review.id}
                        review={review}
                        onLikeClick={handleLikeClick}
                        onImageClick={handleViewAllImages}
                        isLastInColumn={columnIndex === columnArray.length - 1}
                      />
                    ))}
                  </Column>
                  <Column>
                    {rightColumnReviews.map((review, columnIndex, columnArray) => (
                      <ReviewCard
                        key={review.id}
                        review={review}
                        onLikeClick={handleLikeClick}
                        onImageClick={handleViewAllImages}
                        isLastInColumn={columnIndex === columnArray.length - 1}
                      />
                    ))}
                  </Column>
                </>
              )}
            </ReviewList>

            <Divider style={{ margin: '16px 0' }} />

            <ReviewPagination currentPage={currentPage} totalPages={filteredTotalPages} onPageChange={setCurrentPage} />
          </>
        )}
        {(isPageMetaLoading || isReviewsLoading) && <Spinner />}

        <WriteReviewButton onClick={handleClickWriteReview} disabled={isCreatingReview}>
          Write a review
        </WriteReviewButton>
      </ContentWrapper>

      {isPC && (
        <ReviewWritePcModal
          isOpen={isPcWriteModalOpen}
          productImage={productImage}
          onClose={handleCloseWriteModal}
          onSubmit={handlePcReviewSubmit}
        />
      )}
      {!isPC && (
        <ReviewWriteMobileModal
          isOpen={isMobileWriteOpen}
          productImage={productImage}
          onClose={handleCloseMobileWriteModal}
          onSubmit={handleMobileReviewSubmit}
        />
      )}

      <ImageGalleryModal isOpen={isImageGalleryOpen} onClose={handleCloseImageGallery} images={customerImages} />
    </Container>
  );
};

export default HomePageReviewSection;
